import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { setOwnerTenant } from '../api/auth';
import { meQueryKey } from '../api/me';
import type { TenantOverview } from '../api/types';

/** Запросы владельца продукта. */

export type TenantsResponse = {
  platformTenant: string;
  platformBot: string | null;
  tenants: TenantOverview[];
};

export type Invite = { code: string; displayName: string | null; expiresAt: number | null };

export type CreatedTenant = {
  tenant: { id: string; title: string };
  invite: Invite;
  /** null, если бота нет ни у организации, ни у платформы — тогда ссылку собрать не из чего. */
  link: string | null;
  linkHint: string | null;
  overview: TenantOverview;
};

export type PlatformContact = { tg_id: number; username: string | null; first_name: string; last_seen_at: number };

export type Subscription = { plan: string; status: string; trial_ends_at: number | null };

/**
 * Владелец продукта. `suspended_at` не пуст — права приостановлены: строка
 * осталась, доступа нет. `isMe` проставляет сервер: свою строку не трогают.
 */
export type SuperAdmin = {
  tg_id: number;
  name: string;
  /** Ник знаем только про тех, кто писал боту платформы. Пусто — значит не писал. */
  username: string | null;
  created_at: number;
  suspended_at: number | null;
  isMe: boolean;
};

export const ownerKeys = {
  tenants: ['super', 'tenants'] as const,
  tenant: (id: string) => ['super', 'tenant', id] as const,
  contacts: ['super', 'contacts'] as const,
  admins: ['super', 'admins'] as const,
};

function useRefreshOwner() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['super'] });
    void qc.invalidateQueries({ queryKey: meQueryKey });
  };
}

export const useTenants = () =>
  useQuery({ queryKey: ownerKeys.tenants, queryFn: () => api.get<TenantsResponse>('/api/super/tenants') });

export const useTenant = (id: string | null) =>
  useQuery({
    queryKey: ownerKeys.tenant(id ?? ''),
    queryFn: () => api.get<TenantOverview>(`/api/super/tenants/${id}`),
    enabled: !!id,
  });

/** Кому бот уже может писать: он не пишет первым тем, кто ему не писал. */
export const usePlatformContacts = () =>
  useQuery({ queryKey: ownerKeys.contacts, queryFn: () => api.get<PlatformContact[]>('/api/super/contacts') });

export const useSuperAdmins = () =>
  useQuery({ queryKey: ownerKeys.admins, queryFn: () => api.get<SuperAdmin[]>('/api/super/admins') });

export function useCreateTenant() {
  const refresh = useRefreshOwner();
  return useMutation({
    mutationFn: (b: { title: string; slug?: string; tz?: string; adminName?: string; adminUsername?: string; directions?: string[] }) =>
      api.post<CreatedTenant>('/api/super/tenants', b),
    onSuccess: refresh,
  });
}

export function useNewInvite() {
  const refresh = useRefreshOwner();
  return useMutation({
    mutationFn: (v: { id: string; displayName?: string; tgUsername?: string }) =>
      api.post<{ invite: Invite; link: string | null }>(`/api/super/tenants/${v.id}/invite`, {
        displayName: v.displayName, tgUsername: v.tgUsername,
      }),
    onSuccess: refresh,
  });
}

/**
 * `sent: false` — не ошибка, а нормальная ветка: бот не может написать первым.
 * Вместе с отказом всегда приходит ссылка, которую владелец отправит сам.
 */
export function useSendInvite() {
  const refresh = useRefreshOwner();
  return useMutation({
    mutationFn: (v: { id: string; chatId: number; code?: string; note?: string }) =>
      api.post<{ sent: boolean; link: string; code: string; error?: string }>(`/api/super/tenants/${v.id}/send-invite`, {
        chatId: v.chatId, code: v.code, note: v.note,
      }),
    onSuccess: refresh,
  });
}

export function useConnectBot() {
  const refresh = useRefreshOwner();
  return useMutation({
    mutationFn: (v: { id: string; botToken: string }) =>
      api.put<{ username: string | null }>(`/api/super/tenants/${v.id}/bot`, { botToken: v.botToken }),
    onSuccess: refresh,
  });
}

export function useSetSubscription() {
  const refresh = useRefreshOwner();
  return useMutation({
    mutationFn: (v: { id: string; status?: string; plan?: string }) =>
      api.patch<Subscription>(`/api/super/tenants/${v.id}/subscription`, { status: v.status, plan: v.plan }),
    onSuccess: refresh,
  });
}

export function useSuspendTenant() {
  const refresh = useRefreshOwner();
  return useMutation({
    mutationFn: (v: { id: string; suspended: boolean }) =>
      api.post<TenantOverview>(`/api/super/tenants/${v.id}/suspend`, { suspended: v.suspended }),
    onSuccess: refresh,
  });
}

/**
 * Войти в организацию администратором.
 *
 * Сервер сначала пишет вход в её журнал и только потом подтверждает — иначе
 * получился бы доступ без следа. Дальше приложение перезагружается уже с
 * X-Tenant этой организации: роль и набор экранов меняются целиком.
 */
export function useEnterTenant() {
  return useMutation({
    mutationFn: (id: string) => api.post<{ tenant: { id: string; title: string } }>(`/api/super/tenants/${id}/enter`),
    onSuccess: (res) => {
      setOwnerTenant(res.tenant.id);
      window.location.assign('/admin');
    },
  });
}

export function useAddSuperAdmin() {
  const refresh = useRefreshOwner();
  return useMutation({
    mutationFn: (b: { tgId: number; name: string }) => api.post<SuperAdmin[]>('/api/super/admins', b),
    onSuccess: refresh,
  });
}

/**
 * Приостановка прав и удаление — разные мутации, а не одна с флагом: у них
 * разная цена ошибки, и объединять их значило бы решать судьбу строки
 * значением параметра.
 *
 * Обновляем `me` вместе со списком: сняв права себе подобному, владелец меняет
 * не только эту страницу — роль приходит из `GET /api/me`.
 */
export function useSuperAdminMutations() {
  const refresh = useRefreshOwner();
  const suspend = useMutation({
    mutationFn: (v: { tgId: number; suspended: boolean }) =>
      api.patch<SuperAdmin[]>(`/api/super/admins/${v.tgId}`, { suspended: v.suspended }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (tgId: number) => api.delete<SuperAdmin[]>(`/api/super/admins/${tgId}`),
    onSuccess: refresh,
  });
  return { suspend, remove };
}
